// frontend/src/theme/index.js
// Sistema de tokens neutro Tecno World / Freeo / Genérico.
// Azul marino primario compartido entre marcas; el acento varía según marca activa.

// ─────────────────────────────────────────────────────────────────────────────
// Paletas por marca
// ─────────────────────────────────────────────────────────────────────────────

import logoTecno from '../assets/logo-tecno.png';
import logoFreeo from '../assets/logo-freeo.png';

export const BRANDS = {
  generic: {
    key: 'generic',
    name: 'Optimus',
    primary: '#15306b',
    primaryDeep: '#0d224f',
    primaryHover: '#244a9c',
    primaryActive: '#0f2557',
    primarySoft: '#e8edf7',
    accent: '#d97347',
    accentSoft: '#fbeee5',
    logo: null,
  },
  tecno: {
    key: 'tecno',
    name: 'Tecno World',
    primary: '#1d3f9e',
    primaryDeep: '#142d75',
    primaryHover: '#2a4ba8',
    primaryActive: '#152868',
    primarySoft: '#e7ecf8',
    accent: '#d8262c',
    accentSoft: '#fae5e6',
    logo: logoTecno,
  },
  freeo: {
    key: 'freeo',
    name: 'Freeo',
    primary: '#163e8f',
    primaryDeep: '#0e2b69',
    primaryHover: '#2849a8',
    primaryActive: '#152968',
    primarySoft: '#e6ecf7',
    accent: '#e87b1f',
    accentSoft: '#fcefdf',
    logo: logoFreeo,
  },
};

export const DEFAULT_BRAND = 'generic';

// ─────────────────────────────────────────────────────────────────────────────
// Construye el tema de Ant Design 5 a partir de la marca activa
// ─────────────────────────────────────────────────────────────────────────────

export function buildAntdTheme(brandKey = DEFAULT_BRAND, dark = false) {
  const brand = BRANDS[brandKey] || BRANDS[DEFAULT_BRAND];

  return {
    token: {
      // Color primario
      colorPrimary: brand.primary,
      colorLink: brand.primary,
      colorInfo: brand.primary,

      // Estado
      colorSuccess: '#1f8a5a',
      colorWarning: '#d97706',
      colorError: '#c84545',

      // Tipografía
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 13,
      fontSizeHeading1: 24,
      fontSizeHeading2: 20,
      fontSizeHeading3: 16,
      fontSizeHeading4: 14,

      // Forma — bordes algo más rectos (look industrial/utility)
      borderRadius: 4,
      borderRadiusLG: 6,
      borderRadiusSM: 3,

      // Densidad
      controlHeight: 32,
      controlHeightSM: 26,

      // Neutros
      colorBgLayout: dark ? '#0e1018' : '#f5f6f8',
      colorBgContainer: dark ? '#181c28' : '#ffffff',
      colorBorder: dark ? '#252b3d' : '#e5e7eb',
      colorBorderSecondary: dark ? '#1d2540' : '#eef0f4',
      colorText: dark ? '#e3e6ee' : '#1a1d27',
      colorTextSecondary: dark ? '#9ba3b8' : '#5b6378',

      // Sombras suaves (look industrial)
      boxShadow: '0 1px 2px 0 rgba(15, 23, 42, 0.04), 0 1px 3px 0 rgba(15, 23, 42, 0.06)',
      boxShadowSecondary: '0 4px 12px rgba(15, 23, 42, 0.08)',
    },
    components: {
      Layout: {
        headerBg: '#ffffff',
        headerColor: dark ? '#e3e6ee' : '#1a1d27',
        headerHeight: 56,
        headerPadding: '0 20px',
        siderBg: '#0d1426',          // Sider azul marino oscuro (compartido)
        bodyBg: dark ? '#0e1018' : '#f5f6f8',
        triggerBg: '#0a1020',
        triggerColor: '#9aa3bd',
      },
      Menu: {
        // Sider menu (dark theme override)
        darkItemBg: '#0d1426',
        darkSubMenuItemBg: '#0d1426',
        darkItemSelectedBg: brand.primary,
        darkItemSelectedColor: '#ffffff',
        darkItemHoverBg: '#152040',
        darkItemHoverColor: '#ffffff',
        darkItemColor: '#9aa3bd',
        itemHeight: 36,
        itemMarginInline: 6,
        iconSize: 14,
      },
      Button: {
        primaryShadow: 'none',
        defaultShadow: 'none',
        fontWeight: 500,
        controlHeight: 32,
      },
      Table: {
        headerBg: dark ? '#181c28' : '#fafbfc',
        headerColor: dark ? '#9ba3b8' : '#5b6378',
        headerSplitColor: 'transparent',
        rowHoverBg: dark ? '#1d2540' : '#f5f7fa',
        cellPaddingBlock: 8,
        cellPaddingInline: 10,
        cellPaddingBlockSM: 6,
        cellPaddingInlineSM: 8,
        fontSize: 12.5,
        fontWeightStrong: 600,
      },
      Card: {
        borderRadiusLG: 6,
        paddingLG: 16,
        headerBg: 'transparent',
        headerFontSize: 14,
      },
      Input: {
        controlHeight: 32,
        paddingInline: 10,
      },
      Select: {
        controlHeight: 32,
      },
      Form: {
        itemMarginBottom: 12,
        verticalLabelPadding: '0 0 4px',
        labelColonMarginInlineEnd: 4,
        labelColor: dark ? '#9ba3b8' : '#5b6378',
        labelFontSize: 12,
      },
      Breadcrumb: {
        fontSize: 12,
        itemColor: dark ? '#9ba3b8' : '#5b6378',
        linkColor: dark ? '#9ba3b8' : '#5b6378',
        lastItemColor: dark ? '#e3e6ee' : '#1a1d27',
        separatorColor: dark ? '#5a6481' : '#9ba3b8',
      },
      Tag: {
        fontSize: 10.5,
        defaultBg: dark ? '#1d2540' : '#eef0f4',
      },
      Badge: {
        fontSize: 10,
      },
      Modal: {
        borderRadiusLG: 6,
        headerBg: 'transparent',
      },
      Tabs: {
        cardBg: 'transparent',
        itemSelectedColor: brand.primary,
        itemHoverColor: brand.primary,
        inkBarColor: brand.primary,
        fontSize: 13,
      },
    },
  };
}
